/**
 * Quote Form JavaScript
 * Handles form validation, product items, cart import, file upload with locations, and Supabase API submission
 */

(function () {
    'use strict';

    const API_ENDPOINT = 'https://crtmzyzchtvcrkkhaipv.supabase.co/functions/v1/shopify-quote-webhook';

    let productItemIndex = 1;
    let logoItemIndex = 0;
    let logos = []; // Array of { file, location, pieces }

    // DOM Elements
    const form = document.getElementById('quote-form');
    const successMessage = document.getElementById('quote-form-success');
    const errorMessage = document.getElementById('quote-form-error');
    const errorText = document.getElementById('quote-form-error-text');
    const submitBtn = document.getElementById('quote-submit-btn');
    const addProductBtn = document.getElementById('add-product-btn');
    const productContainer = document.getElementById('product-items-container');
    const importCartBtn = document.getElementById('import-cart-btn');
    const addLogoBtn = document.getElementById('add-logo-btn');
    const logosContainer = document.getElementById('logos-container');
    const nifRequiredMarker = document.getElementById('nif-required-marker');

    if (!form) return;

    // Get logo locations from translations
    let logoLocations = [];
    try {
        const locationsData = document.getElementById('logo-locations-data');
        if (locationsData) {
            const locationsStr = JSON.parse(locationsData.textContent);
            logoLocations = locationsStr.split(',').map(s => s.trim());
        }
    } catch (e) {
        logoLocations = ['Peito esquerdo', 'Peito direito', 'Costas', 'Manga esquerda', 'Manga direita', 'Bolso', 'Outro'];
    }

    // Initialize
    init();

    function init() {
        // Form submit handler
        form.addEventListener('submit', handleSubmit);

        // Add product button
        if (addProductBtn) {
            addProductBtn.addEventListener('click', addProductItem);
        }

        // Import cart button
        if (importCartBtn) {
            importCartBtn.addEventListener('click', importFromCart);
        }

        // Add logo button
        if (addLogoBtn) {
            addLogoBtn.addEventListener('click', addLogoItem);
        }

        // Client type toggle - handle NIF requirement
        const clientTypeRadios = form.querySelectorAll('input[name="client_type"]');
        clientTypeRadios.forEach(radio => {
            radio.addEventListener('change', handleClientTypeChange);
        });

        // Initial client type check
        handleClientTypeChange();

        // Remove item buttons (delegated)
        document.addEventListener('click', function (e) {
            if (e.target.closest('.remove-item-btn')) {
                removeProductItem(e.target.closest('.product-item'));
            }
            if (e.target.closest('.remove-logo-btn')) {
                removeLogoItem(e.target.closest('.logo-item'));
            }
        });

        // File input changes
        document.addEventListener('change', function (e) {
            if (e.target.matches('.logo-file-input')) {
                handleLogoFileChange(e.target);
            }
        });
    }

    // Client Type Change - Toggle NIF requirement and Company Name visibility
    function handleClientTypeChange() {
        const clientType = form.querySelector('input[name="client_type"]:checked')?.value || 'company';
        const nifInput = document.getElementById('quote-nif');
        const companyNameContainer = document.getElementById('company-name-container');
        const companyNameInput = document.getElementById('quote-company-name');

        if (clientType === 'company') {
            // Show company name field and make NIF required
            if (nifRequiredMarker) nifRequiredMarker.style.display = 'inline';
            if (nifInput) nifInput.setAttribute('data-required', 'true');
            if (companyNameContainer) companyNameContainer.style.display = 'block';
            if (companyNameInput) companyNameInput.setAttribute('required', 'true');
        } else {
            // Hide company name field and make NIF optional
            if (nifRequiredMarker) nifRequiredMarker.style.display = 'none';
            if (nifInput) nifInput.removeAttribute('data-required');
            if (companyNameContainer) companyNameContainer.style.display = 'none';
            if (companyNameInput) {
                companyNameInput.removeAttribute('required');
                companyNameInput.value = ''; // Clear value when hidden
            }
        }
    }

    // Form Submission
    async function handleSubmit(e) {
        e.preventDefault();

        // Honeypot check - if filled, it's likely a bot
        const honeypot = form.querySelector('[name="website"]');
        if (honeypot && honeypot.value) {
            console.warn('Bot detected via honeypot');
            showSuccess(); // Fake success to confuse bots
            return;
        }

        // Validate form
        if (!validateForm()) {
            return;
        }

        // Show loading state
        setLoadingState(true);
        hideMessages();

        // Collect form data as FormData (not JSON)
        const apiData = collectFormData();

        try {
            const response = await fetch(API_ENDPOINT, {
                method: 'POST',
                body: apiData
            });

            let data;
            try {
                data = await response.json();
            } catch (jsonError) {
                data = {};
            }

            if (response.ok) {
                showSuccess();
                form.reset();
                resetProductItems();
                resetLogos();
            } else {
                showError(data.error || 'Erro ao enviar pedido. Por favor tente novamente.');
            }
        } catch (error) {
            console.error('Quote form error:', error);
            showError('Erro de comunicação. Por favor tente novamente.');
        } finally {
            setLoadingState(false);
        }
    }

    // Collect form data as FormData for file upload support
    function collectFormData() {
        const formData = new FormData(form);
        const apiData = new FormData();

        // Map form fields to API expected names
        apiData.append('customer_name', formData.get('customer_name') || '');
        apiData.append('customer_email', formData.get('customer_email') || '');
        apiData.append('customer_phone', formData.get('customer_phone') || '');
        apiData.append('client_type', formData.get('client_type') || 'company');
        apiData.append('company_name', formData.get('company_name') || ''); // New field for company name
        apiData.append('nif', formData.get('nif') || '');
        apiData.append('address', formData.get('address') || '');
        apiData.append('postal_code', formData.get('postal_code') || '');
        apiData.append('city', formData.get('city') || ''); // New field for city
        apiData.append('country', formData.get('country') || '');
        apiData.append('notes', formData.get('notes') || '');

        // Collect product items as JSON string
        const productItems = document.querySelectorAll('.product-item');
        const items = [];
        productItems.forEach((item) => {
            const product = item.querySelector(`[name^="items"][name$="[product]"]`)?.value;
            const reference = item.querySelector(`[name^="items"][name$="[reference]"]`)?.value;
            const quantity = item.querySelector(`[name^="items"][name$="[quantity]"]`)?.value;
            const color = item.querySelector(`[name^="items"][name$="[color]"]`)?.value;
            const size = item.querySelector(`[name^="items"][name$="[size]"]`)?.value;
            const image = item.querySelector(`[name^="items"][name$="[image]"]`)?.value;

            if (product && quantity) {
                items.push({
                    product: product,
                    reference: reference || '',
                    quantity: parseInt(quantity, 10) || 1,
                    color: color || '',
                    size: size || '',
                    image: image || ''
                });
            }
        });
        apiData.append('details', JSON.stringify({ items: items }));

        // Collect logos with their metadata
        const logosData = [];
        const logoItems = document.querySelectorAll('.logo-item');
        let logoIndex = 0;

        logoItems.forEach((item) => {
            const fileInput = item.querySelector('.logo-file-input');
            const location = item.querySelector('.logo-location-select')?.value || '';
            const pieces = item.querySelector('.logo-pieces-input')?.value || '';

            if (fileInput && fileInput.files.length > 0) {
                const file = fileInput.files[0];
                apiData.append('files', file);
                logosData.push({
                    index: logoIndex,
                    location: location,
                    pieces: pieces
                });
                logoIndex++;
            }
        });

        // Add logos metadata
        if (logosData.length > 0) {
            apiData.append('logos_metadata', JSON.stringify(logosData));
        }

        return apiData;
    }

    // Form Validation
    function validateForm() {
        let isValid = true;
        clearErrors();

        const clientType = form.querySelector('input[name="client_type"]:checked')?.value || 'company';

        const requiredFields = [
            { name: 'customer_name', label: 'Nome' },
            { name: 'customer_email', label: 'Email' },
            { name: 'customer_phone', label: 'Telefone' },
            { name: 'address', label: 'Morada' },
            { name: 'postal_code', label: 'Código Postal' },
            { name: 'city', label: 'Cidade' },
            { name: 'country', label: 'País' }
        ];

        // Company name and NIF only required for companies
        if (clientType === 'company') {
            requiredFields.push({ name: 'company_name', label: 'Nome da Empresa' });
            requiredFields.push({ name: 'nif', label: 'NIF' });
        }

        requiredFields.forEach(field => {
            const input = form.querySelector(`[name="${field.name}"]`);
            if (input && !input.value.trim()) {
                showFieldError(field.name, `${field.label} é obrigatório`);
                isValid = false;
            }
        });

        // Email validation
        const emailInput = form.querySelector('[name="customer_email"]');
        if (emailInput && emailInput.value) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(emailInput.value)) {
                showFieldError('customer_email', 'Email inválido');
                isValid = false;
            }
        }

        // Validate at least one product
        const firstProductInput = form.querySelector('[name="items[0][product]"]');
        if (firstProductInput && !firstProductInput.value.trim()) {
            firstProductInput.classList.add('error');
            isValid = false;
        }

        if (!isValid) {
            // Scroll to first error
            const firstError = form.querySelector('.error');
            if (firstError) {
                firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }

        return isValid;
    }

    function showFieldError(fieldName, message) {
        const input = form.querySelector(`[name="${fieldName}"]`);
        const errorSpan = form.querySelector(`[data-field="${fieldName}"]`);

        if (input) {
            input.classList.add('error');
        }
        if (errorSpan) {
            errorSpan.textContent = message;
        }
    }

    function clearErrors() {
        form.querySelectorAll('.error').forEach(el => el.classList.remove('error'));
        form.querySelectorAll('.field-error').forEach(el => el.textContent = '');
    }

    // Product Items
    // O novo artigo é um clone do primeiro (mantém etiquetas/placeholders já traduzidos pelo Liquid)
    const PRODUCT_FIELDS = ['product', 'reference', 'color', 'size', 'quantity', 'image'];

    function fillProductItem(item, index, productData) {
        item.dataset.index = index;
        PRODUCT_FIELDS.forEach((field) => {
            const input = item.querySelector(`[name^="items"][name$="[${field}]"]`);
            if (!input) return;
            input.name = `items[${index}][${field}]`;
            input.classList.remove('error');
            if (field === 'quantity') {
                input.value = productData && productData.quantity ? productData.quantity : 1;
            } else {
                input.value = (productData && productData[field]) || '';
            }
        });
        // Miniatura do artigo (só quando importado do carrinho)
        const thumb = item.querySelector('.pf-thumb');
        const img = thumb ? thumb.querySelector('img') : null;
        const imageUrl = (productData && productData.image) || '';
        if (thumb && img) {
            if (imageUrl) {
                img.src = imageUrl;
                thumb.hidden = false;
            } else {
                img.removeAttribute('src');
                thumb.hidden = true;
            }
        }
    }

    // URL da imagem do carrinho reduzida via CDN da Shopify
    function thumbUrl(item) {
        const url = item.image || (item.featured_image && item.featured_image.url) || '';
        if (!url) return '';
        return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=120';
    }

    function addProductItem(productData = null) {
        const first = productContainer.querySelector('.product-item');
        if (!first) return;
        const item = first.cloneNode(true);
        fillProductItem(item, productItemIndex, productData);
        productContainer.appendChild(item);
        productItemIndex++;
        updateRemoveButtons();
    }

    function removeProductItem(item) {
        if (!item) return;
        item.remove();
        updateRemoveButtons();
    }

    function updateRemoveButtons() {
        const items = document.querySelectorAll('.product-item');
        items.forEach((item, idx) => {
            const removeBtn = item.querySelector('.remove-item-btn');
            if (removeBtn) {
                removeBtn.style.display = items.length > 1 ? 'flex' : 'none';
            }
        });
    }

    function resetProductItems() {
        const items = document.querySelectorAll('.product-item');
        items.forEach((item, idx) => {
            if (idx > 0) item.remove();
        });
        // Clear first item
        const firstItem = document.querySelector('.product-item');
        if (firstItem) {
            fillProductItem(firstItem, 0, null);
        }
        productItemIndex = 1;
        updateRemoveButtons();
    }

    // Import from Cart
    // Nomes da opção de cor: os mesmos das Theme settings (swatches); tamanho por expressão regular
    const colorOptionNames = (form.dataset.colorOptions || 'color,colour,cor,couleur,farbe,colore,kleur')
        .toLowerCase().split(',').map(s => s.trim()).filter(Boolean);
    const SIZE_RE = /tamanho|size|talla|taille|gr[öo]e?[sß]+e|taglia|maat|tamaño/i;

    function splitVariantOptions(item) {
        const result = { color: '', size: '' };
        const opts = Array.isArray(item.options_with_values) ? item.options_with_values : [];
        const leftovers = [];
        opts.forEach((opt) => {
            const name = String(opt.name || '').toLowerCase().trim();
            const value = String(opt.value || '').trim();
            if (!value || value === 'Default Title') return;
            if (colorOptionNames.includes(name) && !result.color) {
                result.color = value;
            } else if (SIZE_RE.test(name) && !result.size) {
                result.size = value;
            } else {
                leftovers.push(value);
            }
        });
        // Opções não identificadas: 1.ª como cor, 2.ª como tamanho (caso a loja use outros nomes)
        if (!result.color && leftovers.length) result.color = leftovers.shift();
        if (!result.size && leftovers.length) result.size = leftovers.shift();
        if (!opts.length && item.variant_title && item.variant_title !== 'Default Title') {
            const parts = item.variant_title.split(' / ');
            result.color = parts[0] || '';
            result.size = parts[1] || '';
        }
        return result;
    }

    async function importFromCart() {
        try {
            const response = await fetch('/cart.js');
            const cart = await response.json();

            if (!cart.items || cart.items.length === 0) {
                alert(importCartBtn.dataset.emptyMsg || 'O seu carrinho está vazio.');
                return;
            }

            // Clear existing items except first
            resetProductItems();

            // Add cart items
            cart.items.forEach((item, idx) => {
                const variant = splitVariantOptions(item);
                const productData = {
                    product: item.product_title,
                    reference: item.sku || '',
                    quantity: item.quantity,
                    color: variant.color,
                    size: variant.size,
                    image: thumbUrl(item)
                };

                if (idx === 0) {
                    const firstItem = document.querySelector('.product-item');
                    if (firstItem) fillProductItem(firstItem, 0, productData);
                } else {
                    addProductItem(productData);
                }
            });

            // Show success feedback
            importCartBtn.classList.add('import-success');
            setTimeout(() => importCartBtn.classList.remove('import-success'), 2000);

        } catch (error) {
            console.error('Error importing cart:', error);
            alert(importCartBtn.dataset.errorMsg || 'Erro ao importar carrinho.');
        }
    }

    // Logo Items
    // Textos traduzidos pelo Liquid (#quote-form-i18n); PT como alternativa
    let i18n = {
        logo: 'Logótipo', file: 'Ficheiro do Logótipo', location: 'Localização',
        pieces: 'Peças a Aplicar', piecesPlaceholder: 'Ex: Todas as túnicas, T-Shirts...',
        select: 'Selecione...', remove: 'Remover'
    };
    try {
        const i18nData = document.getElementById('quote-form-i18n');
        if (i18nData) i18n = Object.assign(i18n, JSON.parse(i18nData.textContent));
    } catch (e) { /* mantém PT */ }

    function escapeHtml(str) {
        return String(str).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    }

    function addLogoItem() {
        const locationOptions = logoLocations.map(loc =>
            `<option value="${escapeHtml(loc)}">${escapeHtml(loc)}</option>`
        ).join('');
        const id = `quote-logo-${logoItemIndex}`;

        const template = `
      <div class="logo-item" data-index="${logoItemIndex}">
        <div class="logo-item-header">
          <span class="logo-number">${escapeHtml(i18n.logo)} ${logosContainer.querySelectorAll('.logo-item').length + 1}</span>
          <button type="button" class="remove-logo-btn">
            <span aria-hidden="true">×</span> ${escapeHtml(i18n.remove)}
          </button>
        </div>
        <div class="logo-fields">
          <div class="qr-field">
            <label class="form-label" for="${id}-file">${escapeHtml(i18n.file)} *</label>
            <div class="logo-upload-wrapper">
              <input type="file" id="${id}-file" class="logo-file-input" accept=".png,.jpg,.jpeg,.svg,.pdf" data-index="${logoItemIndex}">
              <span class="logo-filename"></span>
            </div>
          </div>
          <div class="qr-field">
            <label class="form-label" for="${id}-location">${escapeHtml(i18n.location)}</label>
            <select id="${id}-location" class="logo-location-select">
              <option value="">${escapeHtml(i18n.select)}</option>
              ${locationOptions}
            </select>
          </div>
          <div class="qr-field">
            <label class="form-label" for="${id}-pieces">${escapeHtml(i18n.pieces)}</label>
            <input type="text" id="${id}-pieces" class="logo-pieces-input" placeholder="${escapeHtml(i18n.piecesPlaceholder)}">
          </div>
        </div>
      </div>
    `;

        logosContainer.insertAdjacentHTML('beforeend', template);
        logoItemIndex++;
    }

    function removeLogoItem(item) {
        if (!item) return;
        item.remove();
        // Renumber remaining logos
        document.querySelectorAll('.logo-item').forEach((logoItem, idx) => {
            logoItem.querySelector('.logo-number').textContent = `${i18n.logo} ${idx + 1}`;
        });
    }

    function resetLogos() {
        logosContainer.innerHTML = '';
        logoItemIndex = 0;
    }

    function handleLogoFileChange(input) {
        const wrapper = input.closest('.logo-upload-wrapper');
        const filenameSpan = wrapper.querySelector('.logo-filename');

        if (input.files.length > 0) {
            const file = input.files[0];
            const maxSize = 5 * 1024 * 1024; // 5MB
            const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'application/pdf'];

            if (!allowedTypes.includes(file.type)) {
                alert('Formato de ficheiro não suportado. Use PNG, JPG, SVG ou PDF.');
                input.value = '';
                if (filenameSpan) filenameSpan.textContent = '';
                return;
            }
            if (file.size > maxSize) {
                alert('Ficheiro demasiado grande. O tamanho máximo é 5MB.');
                input.value = '';
                if (filenameSpan) filenameSpan.textContent = '';
                return;
            }

            if (filenameSpan) filenameSpan.textContent = file.name;
        } else {
            if (filenameSpan) filenameSpan.textContent = '';
        }
    }

    // UI State
    function setLoadingState(loading) {
        if (loading) {
            submitBtn.disabled = true;
            submitBtn.querySelector('.btn-text').style.display = 'none';
            submitBtn.querySelector('.btn-loading').style.display = 'inline-flex';
        } else {
            submitBtn.disabled = false;
            submitBtn.querySelector('.btn-text').style.display = 'inline';
            submitBtn.querySelector('.btn-loading').style.display = 'none';
        }
    }

    function hideMessages() {
        successMessage.style.display = 'none';
        errorMessage.style.display = 'none';
    }

    function showSuccess() {
        successMessage.style.display = 'block';
        form.style.display = 'none';
        window.scrollTo({ top: successMessage.offsetTop - 100, behavior: 'smooth' });
    }

    function showError(message) {
        errorText.textContent = message;
        errorMessage.style.display = 'block';
        window.scrollTo({ top: errorMessage.offsetTop - 100, behavior: 'smooth' });
    }

})();
